/**
 * KissKissBankBank's top collections, frozen.
 *
 * KKBB stopped taking projects, so this list can never change: it was read once from the KKBB
 * GraphQL API and committed. That is the whole reason it is a file rather than a fetch — there is
 * no live source left to call, and nothing to keep in sync.
 *
 * It feeds exactly one of the two Statistiques boards: "Top 10 des collectes de tous les temps",
 * which mixes these with live Ulule projects and ranks the lot by amount. The page's other board,
 * "Les collectes les plus populaires du moment", is Ulule-only — currently-funding projects on our
 * own popularity sort — and must never include KKBB, whose projects all ended.
 *
 * Ordered by amount, which is the only order the mixed board needs.
 */
import kkbb from './kkbbProjects.json'

export type KkbbProject = {
  name: string
  /** Public project page; KKBB still serves these. */
  url: string
  /** Total collected, in euros. */
  amountEur: number
  /** Distinct backers (KKBB's `kissbankersCount`), not its order or contribution count. */
  backersCount: number
  /**
   * Share of the goal reached, in percent (4541 = 4 541 %). Part of the capture rather than of a
   * board: no row shows it. Kept because the source API is gone and cannot be asked again.
   */
  fundingPercent: number
  /** KKBB's own category labels, most significant first. */
  categories: string[]
  /** File name under `assets/kkbb/`; resolved by `kkbbImageUrl`. */
  image: string
}

export type KkbbSnapshot = {
  /** When the list was read from the API, ISO date. */
  capturedAt: string
  source: string
  /** Sorted by `amountEur`, descending — `kkbbProjects.test.ts` holds that. */
  projects: KkbbProject[]
}

/** Annotated, so a malformed entry is a type error, not a broken row. */
export const KKBB: KkbbSnapshot = kkbb

/**
 * KKBB's own project images, downsized and committed alongside the data.
 *
 * Its CDN still serves the originals, but its lightest variant is ~360 KB per image — ~2 MB for
 * the rows this page shows. These are 224x160 crops (2x the board's thumb) at ~15 KB each. Like
 * the data, they are frozen: KKBB will not publish new projects.
 */
const IMAGES = import.meta.glob('./assets/kkbb/*.jpg', { eager: true, query: '?url', import: 'default' })

/** Hashed URL for a project's thumbnail, or undefined if the file is missing. */
export function kkbbImageUrl(image: string): string | undefined {
  return IMAGES[`./assets/kkbb/${image}`]
}

/**
 * KKBB's category labels → the Ulule category an Ulule project would carry.
 *
 * Only a project's primary label (`categories[0]`) is ever read, so it lands in exactly one
 * category — the way an Ulule project does through its `main_tag`. That leaves four labels to map,
 * each with an obvious counterpart; the secondary labels in the snapshot (Youtubers, Roman,
 * Investigation, Reportage) would need judgement calls nobody has to make.
 *
 * `kkbbProjects.test.ts` holds that every primary label in the snapshot is mapped. The snapshot is
 * frozen, so that set cannot grow.
 */
export const ULULE_CATEGORY_ID_BY_KKBB_LABEL: Readonly<Record<string, number>> = {
  'Films & vidéos': 4, // Film & vidéo
  Journalisme: 42, // Édition & Média
  'Livre & Édition': 42, // Édition & Média
  Patrimoine: 37, // Patrimoine
}

/** The Ulule category a KKBB project belongs to, or undefined when its primary label is unmapped. */
export function ululeCategoryId(project: KkbbProject): number | undefined {
  const primary = project.categories[0]
  return primary === undefined ? undefined : ULULE_CATEGORY_ID_BY_KKBB_LABEL[primary]
}
